import React, { Fragment, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { TwitterPicker } from "react-color";
import { saveData } from "utils/OGN";

import {
  Button,
  ClickAwayListener,
  Divider,
  Fade,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popper,
  Switch,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  default_label_color,
  default_dice_color
} from "components/Dice/settings";

import {
  default_ambient_light_color,
  default_ambient_light_color_dark,
  default_spot_light_color,
  default_spot_light_color_dark,
  default_desk_color,
  default_desk_color_dark
} from "components/Dice/settings";
import { actions } from "reducer/reducer";

const {
  updateAmbientLightColor: _updateAmbientLightColor,
  updateDeskColor: _updateDeskColor,
  updateDiceColor: _updateDiceColor,
  updateLabelColor: _updateLabelColor,
  updateSpotlightColor: _updateSpotlightColor,
  updateTheme: _updateTheme
} = actions.preferences;
const selectPreferences = createSelector(
  state => state.preferences,
  _.identity
);

const useStyles = makeStyles(theme => ({
  root: {
    display: `flex`,
    justifyContent: `space-around`,
    alignItems: `center`
  },
  listItemText: {
    display: `flex`,
    justifyContent: `space-between`,
    alignItems: `center`,
    width: `250px`,
    maxWidth: `95%`,
    margin: `0 ${theme.spacing(1.5)}px`
  },
  listItemSecondaryText: {
    display: `flex`,
    justifyContent: `space-between`,
    width: `250px`,
    maxWidth: `95%`,
    margin: `0 ${theme.spacing(1.5)}px`
  },
  swatch: {
    width: `${parseInt(theme.typography.body1.fontSize) * 2}rem`,
    minWidth: `${parseInt(theme.typography.body1.fontSize) * 2}rem`,
    height: `${parseInt(theme.typography.body1.fontSize) * 2}rem`,
    overflow: `hidden`,
    marginLeft: theme.spacing(2),
    cursor: `pointer`
  },
  toggle: {
    marginRight: -theme.spacing(1.5)
  },
  popper: {
    zIndex: theme.zIndex.tooltip
  },
  picker: {
    position: "relative",
    left: -12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
    width: 280,
    // Inline styles. TODO: Make fully custom component
    "& .twitter-picker": {
      backgroundColor: "transparent !important",
      boxShadow: "none !important",
      "& div:last-child": {
        padding: "0 !important"
      }
    }
  },
  buttons: {
    marginTop: theme.spacing(2),
    display: `flex`,
    justifyContent: `flex-end`,
    "& :not(:last-child)": {
      marginRight: theme.spacing(1)
    },
    "& .MuiButton-label": {
      marginRight: 0
    }
  }
}));

const colorDescriptionMap = {
  ambientLightColor: `Ambient Light Color`,
  deskColor: `Desktop Color`,
  diceColor: `Dice Color`,
  labelColor: `Dice Label Color`,
  spotlightColor: `Spotlight Color`
};

const themeColorMap = {
  light: {
    ambientLightColor: default_ambient_light_color,
    spotlightColor: default_spot_light_color,
    deskColor: default_desk_color
  },
  dark: {
    ambientLightColor: default_ambient_light_color_dark,
    spotlightColor: default_spot_light_color_dark,
    deskColor: default_desk_color_dark
  }
};

const Settings = () => {
  const classes = useStyles();

  const preferences = useSelector(selectPreferences);
  const { diceColor, labelColor, theme } = preferences;
  const dispatch = useDispatch();

  const [
    updateAmbientLightColor,
    updateDeskColor,
    updateDiceColor,
    updateLabelColor,
    updateSpotlightColor,
    updateTheme
  ] = useMemo(
    () => [
      (...args) => dispatch(_updateAmbientLightColor(...args)),
      (...args) => dispatch(_updateDeskColor(...args)),
      (...args) => dispatch(_updateDiceColor(...args)),
      (...args) => dispatch(_updateLabelColor(...args)),
      (...args) => dispatch(_updateSpotlightColor(...args)),
      (...args) => dispatch(_updateTheme(...args))
    ],
    [dispatch]
  );
  const keyToUpdaterMap = {
    ambientLightColor: updateAmbientLightColor,
    deskColor: updateDeskColor,
    diceColor: updateDiceColor,
    labelColor: updateLabelColor,
    spotlightColor: updateSpotlightColor
  };

  const [errorMessage, setErrorMessage] = useState(``);
  useEffect(() => {
    saveData({ payload: { field: `preferences`, data: preferences } })
      .run()
      .promise()
      .then(_ => {
        setErrorMessage(``);
      })
      .catch(_ => {
        setErrorMessage(`Error saving to OGN: ( ･_･) ﾉ⌒●~*`);
      });
  }, [diceColor, labelColor, theme, preferences]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [colorToChange, setColorToChange] = useState(null);
  const pickerOpen = Boolean(anchorEl);
  const handleSwatchClick = key => evt => {
    setColorToChange(key);
    setAnchorEl(evt.target);
    setSelectedColor(null);
  };

  const [selectedColor, setSelectedColor] = useState(null);
  const colorPreferences = {
    diceColor,
    labelColor
  };
  const handlePickerChange = hex => {
    setSelectedColor(hex);
  };
  const handlePickerCancel = evt => {
    if (!_.includes(evt.target.classList, classes.swatch)) {
      setAnchorEl(null);
    }
  };
  const handlePickerConfirm = () => {
    keyToUpdaterMap[colorToChange]({ color: selectedColor });
    setAnchorEl(null);
  };

  const handleThemeChange = evt => {
    const theme = evt.target.checked ? `dark` : `light`;
    const colors = themeColorMap[theme];
    updateTheme({ theme });

    _.map(colors, (color, key) => {
      keyToUpdaterMap[key]({ color });
    });
  };

  return (
    <Paper>
      <List aria-label="app settings">
        <ListItem>
          <ListItemText
            primary={
              <div className={classes.listItemText}>
                <span>Enable Dark Mode</span>
                <Switch
                  className={classes.toggle}
                  checked={`dark` === theme}
                  onChange={handleThemeChange}
                />
              </div>
            }
          />
        </ListItem>
        <Divider component="li" />
        {_.map(colorPreferences, (color, key, object) => {
          const primaryItem = (
            <div className={classes.listItemText}>
              <span>{colorDescriptionMap[key]}</span>
              <Button
                className={classes.swatch}
                style={{
                  backgroundColor: color
                }}
                onClick={handleSwatchClick(key)}
              />
            </div>
          );
          const secondaryItem = (
            <span className={classes.listItemSecondaryText}>
              Click or tap to change
            </span>
          );

          return (
            <Fragment key={key}>
              <ListItem>
                <ListItemText primary={primaryItem} secondary={secondaryItem} />
              </ListItem>
              {key !== _.keys(object).slice(-1)[0] ? (
                <Divider component="li" />
              ) : undefined}
            </Fragment>
          );
        })}
        {!!errorMessage && (
          <>
            <Divider component="li" />
            <ListItem>
              <ListItemText
                secondary={
                  <span className={classes.listItemSecondaryText}>
                    <Typography variant="body2" color="error">
                      {errorMessage}
                    </Typography>
                  </span>
                }
              />
            </ListItem>
          </>
        )}
      </List>

      <Popper
        className={classes.popper}
        open={pickerOpen}
        anchorEl={anchorEl}
        transition
        placement="bottom"
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handlePickerCancel}>
            <Fade {...TransitionProps}>
              <Paper className={classes.picker}>
                <TwitterPicker
                  color={selectedColor || colorPreferences[colorToChange]}
                  colors={[
                    default_dice_color,
                    default_label_color,
                    "#D9E3F0",
                    "#F47373",
                    "#697689",
                    "#37D67A",
                    "#2CCCE4",
                    "#dce775",
                    "#ff8a65",
                    "#ba68c8"
                  ]}
                  triangle="hide"
                  onChangeComplete={({ hex }) => {
                    handlePickerChange(hex);
                  }}
                />
                <div className={classes.buttons}>
                  <Button onClick={handlePickerCancel}>Cancel</Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePickerConfirm}
                    disabled={!Boolean(selectedColor)}
                  >
                    Select
                  </Button>
                </div>
              </Paper>
            </Fade>
          </ClickAwayListener>
        )}
      </Popper>
    </Paper>
  );
};

export default Settings;
